import { Box, Button } from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { ThumbUpOutlineIcon as ThumbUpAltOutlinedIcon } from "./ThumbUpOutlineIcon";
import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded";
import { actionCountLabel } from "@bw/shared/components";
import type { PostResponse } from "@bw/shared/types/generated";
import { Link as RouterLink } from "react-router-dom";

export function PostActionBar({
  post,
  likeBusy,
  onLike,
  onShare,
  hideComment = false,
  commentSummary,
}: {
  post: PostResponse;
  likeBusy: boolean;
  onLike: () => void;
  onShare?: () => void;
  hideComment?: boolean;
  /** Shown in the Comment slot when the comment link is hidden (post detail). */
  commentSummary?: React.ReactNode;
}) {
  const liked = Boolean(post.liked_by_me);
  const likeCount = actionCountLabel(post.like_count);
  const commentCount = actionCountLabel(post.comment_count);
  const likeLabel = liked ? "Liked" : "Like";

  const action = {
    minWidth: 44,
    minHeight: 44,
    px: 1,
    gap: 0.75,
    color: "text.secondary",
    fontWeight: 600,
  } as const;

  return (
    <Box
      role="group"
      aria-label="Post actions"
      sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, width: "100%" }}
    >
      <Button
        type="button"
        variant="text"
        aria-label={
          likeCount === null ? likeLabel : `${likeLabel} ${likeCount}`
        }
        aria-pressed={liked}
        disabled={likeBusy}
        onClick={onLike}
        sx={{ ...action, ...(liked ? { color: "primary.main" } : null) }}
      >
        {liked ? <ThumbUpAltRoundedIcon fontSize="small" /> : <ThumbUpAltOutlinedIcon fontSize="small" />}
        {likeCount !== null ? <span>{likeCount}</span> : null}
      </Button>
      {!hideComment ? (
        <Button
          component={RouterLink}
          variant="text"
          to={`/community/posts/${post.post_id}`}
          aria-label={
            commentCount === null ? "Comment" : `Comment ${commentCount}`
          }
          sx={action}
        >
          <ChatBubbleOutlineRoundedIcon fontSize="small" />
          {commentCount !== null ? <span>{commentCount}</span> : null}
        </Button>
      ) : commentSummary ? (
        <Box
          sx={{ ...action, display: "inline-flex", alignItems: "center", fontSize: "0.875rem" }}
        >
          <ChatBubbleOutlineRoundedIcon fontSize="small" aria-hidden />
          {commentSummary}
        </Box>
      ) : null}
      {onShare ? (
        <Button
          type="button"
          variant="text"
          aria-label="Share"
          aria-haspopup="dialog"
          onClick={onShare}
          sx={action}
        >
          <SendRoundedIcon fontSize="small" />
        </Button>
      ) : null}
    </Box>
  );
}
